import type {
  HistoricalRealYear,
  HistoricalRealYearWithCape,
} from "../data/historical-returns.js";
import {
  applyWithdrawalStrategy,
  type WithdrawalStrategy,
  type WithdrawalStrategyConfig,
  type WithdrawalState,
} from "./withdrawal-strategies.js";
import {
  applySpendingSmile,
  type SpendingSmileConfig,
} from "./spending-smile.js";
import {
  bondTentAllocation,
  type BondTentConfig,
} from "./bond-tent.js";

/**
 * Stress-test pure helpers. Phase 6 Session 2 + 6 Session 3.
 *
 * Helpers (all pure — no DDB, no I/O, no time):
 *
 *   1. trinityBackTest(...)            — replay portfolio through every
 *                                        rolling 30-year window 1928→today.
 *                                        Default withdrawal: Bengen-constant
 *                                        (6.2 baseline). Optional 6.3
 *                                        overrides: withdrawal_strategy,
 *                                        spending_smile, bond_tent.
 *   2. sequenceOfReturnsScenarios(...) — same returns in three orders
 *                                        (forward / reversed / shuffled).
 *                                        Strategy-agnostic — sequence is
 *                                        the order-effect lesson, not the
 *                                        withdrawal-rule lesson.
 *   3. monteCarloSimulation(...)       — N stochastic futures from a
 *                                        normal(mean, stddev) return
 *                                        distribution. Optional 6.3
 *                                        overrides plumb the same way.
 *   4. compareWithdrawalStrategies(...) — runs Trinity + Monte Carlo for
 *                                        each strategy in `strategies`,
 *                                        returns one row per strategy.
 *                                        Powers the new Compare tab.
 *
 * **Locked positions (Phase 6 Session 2, 2026-05-08):**
 *   - Constant inflation-adjusted withdrawal (Bengen original Trinity
 *     formulation). 6.3 layers Guyton-Klinger / VPW / CAPE / dynamic.
 *   - Real-return formulation: dataset stores CPI-adjusted returns; we
 *     withdraw constant in real (today's-dollars) terms. Mathematically
 *     equivalent to nominal-returns + inflation-adjusted-withdrawal.
 *   - Monte Carlo distribution: normal via Box-Muller. Default σ = 15%
 *     (S&P historical real). Lognormal + bootstrap deferred to Phase 11.
 *   - Default Monte Carlo iterations = 1000 (~30-60ms client-side).
 *   - Mulberry32 PRNG seeded for fixture reproducibility.
 *
 * **Locked positions (Phase 6 Session 3, 2026-05-08):**
 *   - 5 withdrawal strategies via dispatcher in withdrawal-strategies.ts.
 *   - Spending-smile via Blanchett 2014 polynomial fit (smile.ts).
 *   - Bond-tent monotone rise-and-hold glidepath (bond-tent.ts);
 *     Pfau reverse-glide deferred to Phase 11.
 *   - Bond-tent rebalances Monte Carlo per-year μ / σ via weighted blend
 *     of (equity_mean, bond_mean) and uncorrelated (ρ=0) variance sum.
 *
 * **Cents everywhere.** All amounts integer cents. Helpers accept the
 * historical-returns array as a parameter, so they stay data-agnostic and
 * testable with synthetic series.
 *
 * **Web mirrors this** at web/src/lib/util/stress-test.ts per the 5.1 lock.
 */

// ============================================================================
// Trinity Study back-test
// ============================================================================

export interface TrinityBackTestInput {
  readonly portfolio_value_cents: number;
  /** Withdrawal in cents per year, constant in real terms. */
  readonly annual_spend_cents: number;
  /** Equity allocation 0-100. equity + bond must equal 100. */
  readonly equity_pct: number;
  /** Bond allocation 0-100. */
  readonly bond_pct: number;
  /** How many years each rolling window covers. Default 30. */
  readonly horizon_years?: number;
  /** Real-return series (year + equity_real_pct + bond_real_pct). Pass
   *  HISTORICAL_REAL_RETURNS from data/historical-returns.ts. May
   *  optionally carry per-year `cape_value` (HistoricalRealYearWithCape)
   *  to enable the CAPE-based withdrawal strategy. */
  readonly returns:
    | readonly HistoricalRealYear[]
    | readonly HistoricalRealYearWithCape[];
  /** Phase 6 Session 3 — withdrawal-strategy override. Defaults to
   *  bengen_constant (preserves 6.2 baseline). */
  readonly withdrawal_strategy_config?: WithdrawalStrategyConfig;
  /** Headline target withdrawal rate %, used by Guyton-Klinger / dynamic
   *  to compute floor/ceiling triggers. Defaults to
   *  `annual_spend_cents / portfolio_value_cents × 100`. */
  readonly target_withdrawal_rate_pct?: number;
  /** Phase 6 Session 3 — apply Blanchett spending-smile to the year-by-year
   *  spend. When enabled, requires `age_at_retirement`. */
  readonly spending_smile?: SpendingSmileConfig;
  /** Caller's age at year_offset = 0. Required for spending_smile and for
   *  bond_tent + VPW. */
  readonly age_at_retirement?: number;
  /** Phase 6 Session 3 — pre-retirement bond glidepath. When set, the
   *  per-year allocation comes from bondTentAllocation(age, ...) and
   *  overrides the static equity_pct / bond_pct. */
  readonly bond_tent?: BondTentConfig;
  /** Added to each retirement year's spending, by year offset. Positive
   *  for extra costs, negative for income like Social Security. */
  readonly annual_adjustments_cents?: readonly number[];
  /** Tax paid on withdrawals, as % of the gross amount withdrawn. */
  readonly effective_tax_rate_pct?: number;
}

export interface TrinityWindow {
  readonly start_year: number;
  readonly end_year: number;
  /** Cents at end of horizon. Capped at 0 if the portfolio depleted
   *  mid-horizon. */
  readonly ending_balance_cents: number;
  readonly ran_out: boolean;
  /** First year balance went negative. Null when survived. */
  readonly ran_out_year: number | null;
}

export interface TrinityBackTestResult {
  readonly windows: readonly TrinityWindow[];
  /** % of windows that survived the full horizon. 0-100. */
  readonly success_pct: number;
  /** Window with the smallest ending_balance_cents (or first ran-out). */
  readonly worst_window: TrinityWindow | null;
  /** Window with the largest ending_balance_cents. */
  readonly best_window: TrinityWindow | null;
}

/** Gross amount leaving the portfolio to cover a year's spending. */
function withdrawalForYear(
  spendCents: number,
  adjustmentCents: number | undefined,
  taxRatePct: number,
): number {
  const need = Math.max(0, spendCents + (adjustmentCents ?? 0));
  return need / (1 - taxRatePct / 100);
}

export interface HistoricalReturnStats {
  readonly equity_mean_pct: number;
  readonly equity_stddev_pct: number;
  readonly bond_mean_pct: number;
  readonly bond_stddev_pct: number;
}

/**
 * Arithmetic mean and sample standard deviation of a real-return series.
 * Monte Carlo compounds yearly draws, so it needs the arithmetic mean.
 */
export function historicalReturnStats(
  returns: readonly HistoricalRealYear[],
): HistoricalReturnStats {
  const stats = (values: readonly number[]) => {
    const mean = values.reduce((sum, v) => sum + v, 0) / values.length;
    const variance =
      values.reduce((sum, v) => sum + (v - mean) ** 2, 0) /
      (values.length - 1);
    return { mean, stddev: Math.sqrt(variance) };
  };
  const equity = stats(returns.map((r) => r.equity_real_pct));
  const bond = stats(returns.map((r) => r.bond_real_pct));
  return {
    equity_mean_pct: equity.mean,
    equity_stddev_pct: equity.stddev,
    bond_mean_pct: bond.mean,
    bond_stddev_pct: bond.stddev,
  };
}

export function trinityBackTest(
  input: TrinityBackTestInput,
): TrinityBackTestResult {
  const {
    portfolio_value_cents,
    annual_spend_cents,
    equity_pct,
    bond_pct,
    horizon_years = 30,
    returns,
    withdrawal_strategy_config,
    target_withdrawal_rate_pct,
    spending_smile,
    age_at_retirement,
    bond_tent,
    annual_adjustments_cents,
    effective_tax_rate_pct = 0,
  } = input;

  if (Math.abs(equity_pct + bond_pct - 100) > 0.001) {
    throw new Error(
      `trinityBackTest: equity_pct + bond_pct must equal 100 (got ${equity_pct} + ${bond_pct})`,
    );
  }
  if (horizon_years < 1) {
    throw new Error(
      `trinityBackTest: horizon_years must be >= 1 (got ${horizon_years})`,
    );
  }
  if (returns.length < horizon_years) {
    throw new Error(
      `trinityBackTest: need >= ${horizon_years} years of returns (got ${returns.length})`,
    );
  }

  // Headline withdrawal rate for guardrail triggers.
  const targetWrPct =
    target_withdrawal_rate_pct ??
    (portfolio_value_cents > 0
      ? (annual_spend_cents / portfolio_value_cents) * 100
      : 4);

  // Number of complete rolling windows: returns.length - horizon_years + 1.
  const windowCount = returns.length - horizon_years + 1;
  const windows: TrinityWindow[] = [];

  for (let i = 0; i < windowCount; i++) {
    // Bounds proven by the loop condition + dataset-length check above —
    // `i + horizon_years - 1 < returns.length`. Non-null asserts kept tight
    // around individual reads rather than splattered across the function.
    const startYear = returns[i]!.year;
    const endYear = returns[i + horizon_years - 1]!.year;
    let balance = portfolio_value_cents;
    let prevSpend = annual_spend_cents;
    let ranOut = false;
    let ranOutYear: number | null = null;

    for (let h = 0; h < horizon_years; h++) {
      const yr = returns[i + h]!;

      // Per-year allocation: bond-tent overrides static when enabled.
      let eqW = equity_pct / 100;
      let bdW = bond_pct / 100;
      if (bond_tent && bond_tent.enabled && age_at_retirement !== undefined) {
        const alloc = bondTentAllocation({
          age: age_at_retirement + h,
          config: bond_tent,
        });
        eqW = alloc.equity_pct / 100;
        bdW = alloc.bond_pct / 100;
      }

      const blendedReturn =
        eqW * yr.equity_real_pct + bdW * yr.bond_real_pct;
      balance = balance * (1 + blendedReturn / 100);

      // Compute this year's spend via the strategy dispatcher. Defaults
      // to bengen_constant when no config provided (preserves 6.2).
      const baseSpend = annual_spend_cents;
      const stateForStrategy = buildWithdrawalState({
        year_offset: h,
        initial_spend_cents: baseSpend,
        prev_spend_cents: prevSpend,
        current_balance_cents: balance,
        target_withdrawal_rate_pct: targetWrPct,
        age_value:
          age_at_retirement === undefined ? undefined : age_at_retirement + h,
        equity_pct: eqW * 100,
        bond_pct: bdW * 100,
        cape_value: (yr as Partial<HistoricalRealYearWithCape>).cape_value,
      });
      let spend = applyWithdrawalStrategy(
        stateForStrategy,
        withdrawal_strategy_config,
      );

      // Spending smile: scales whatever the strategy produced.
      if (
        spending_smile &&
        spending_smile.enabled &&
        age_at_retirement !== undefined
      ) {
        const smileMultiplier =
          applySmileMultiplier(h, age_at_retirement, spending_smile.curve);
        spend *= smileMultiplier;
      }

      balance -= withdrawalForYear(
        spend,
        annual_adjustments_cents?.[h],
        effective_tax_rate_pct,
      );
      prevSpend = spend;
      if (balance < 0 && !ranOut) {
        ranOut = true;
        ranOutYear = yr.year;
      }
    }

    windows.push({
      start_year: startYear,
      end_year: endYear,
      ending_balance_cents: Math.max(0, Math.round(balance)),
      ran_out: ranOut,
      ran_out_year: ranOutYear,
    });
  }

  const survived = windows.filter((w) => !w.ran_out).length;
  const successPct = (survived / windows.length) * 100;

  // Worst: ran-out wins over survived (rank by ran_out, then by ending balance).
  // Best: largest ending balance among survived; null if none survived.
  let worst: TrinityWindow | null = null;
  let best: TrinityWindow | null = null;
  for (const w of windows) {
    if (worst === null) {
      worst = w;
    } else if (w.ran_out && !worst.ran_out) {
      worst = w;
    } else if (
      w.ran_out === worst.ran_out &&
      w.ending_balance_cents < worst.ending_balance_cents
    ) {
      worst = w;
    }
    if (!w.ran_out) {
      if (best === null || w.ending_balance_cents > best.ending_balance_cents) {
        best = w;
      }
    }
  }

  return {
    windows,
    success_pct: successPct,
    worst_window: worst,
    best_window: best,
  };
}

// ============================================================================
// Sequence-of-returns risk
// ============================================================================

export interface SequenceOfReturnsInput {
  readonly portfolio_value_cents: number;
  readonly annual_spend_cents: number;
  /** Yearly real returns to apply, e.g. [-37, 26, 15, 2, -5, ...]. */
  readonly returns_series: readonly number[];
  /** Seed for the deterministic shuffle. Default 42. */
  readonly seed?: number;
}

export interface BalancePoint {
  /** Year offset from start: 0 = before first year's return, N = after N years. */
  readonly year_offset: number;
  /** Capped at 0 floor. */
  readonly balance_cents: number;
}

export interface SequenceOfReturnsResult {
  readonly forward_path: readonly BalancePoint[];
  readonly reversed_path: readonly BalancePoint[];
  readonly shuffled_path: readonly BalancePoint[];
  /** Geometric mean of the return series — identical across all three
   *  orderings (compound multiplication is commutative; that's the lesson). */
  readonly geometric_mean_pct: number;
}

export function sequenceOfReturnsScenarios(
  input: SequenceOfReturnsInput,
): SequenceOfReturnsResult {
  const { portfolio_value_cents, annual_spend_cents, returns_series, seed = 42 } =
    input;
  if (returns_series.length < 1) {
    throw new Error("sequenceOfReturnsScenarios: returns_series cannot be empty");
  }

  const forward = [...returns_series];
  const reversed = [...returns_series].reverse();
  const shuffled = deterministicShuffle([...returns_series], seed);

  const forwardPath = walkPath(
    portfolio_value_cents,
    annual_spend_cents,
    forward,
  );
  const reversedPath = walkPath(
    portfolio_value_cents,
    annual_spend_cents,
    reversed,
  );
  const shuffledPath = walkPath(
    portfolio_value_cents,
    annual_spend_cents,
    shuffled,
  );

  // Geometric mean: (∏(1 + r_i))^(1/N) - 1, expressed in %.
  let product = 1;
  for (const r of returns_series) {
    product *= 1 + r / 100;
  }
  const geomMean = (Math.pow(product, 1 / returns_series.length) - 1) * 100;

  return {
    forward_path: forwardPath,
    reversed_path: reversedPath,
    shuffled_path: shuffledPath,
    geometric_mean_pct: geomMean,
  };
}

function walkPath(
  start_cents: number,
  spend_cents: number,
  series: readonly number[],
): BalancePoint[] {
  const path: BalancePoint[] = [
    { year_offset: 0, balance_cents: start_cents },
  ];
  let balance = start_cents;
  for (let i = 0; i < series.length; i++) {
    balance = balance * (1 + series[i]! / 100) - spend_cents;
    path.push({
      year_offset: i + 1,
      balance_cents: Math.max(0, Math.round(balance)),
    });
  }
  return path;
}

// Fisher-Yates shuffle with a deterministic PRNG so tests are reproducible.
function deterministicShuffle<T>(arr: T[], seed: number): T[] {
  const rng = mulberry32(seed);
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    const tmp = arr[i]!;
    arr[i] = arr[j]!;
    arr[j] = tmp;
  }
  return arr;
}

// ============================================================================
// Monte Carlo retirement calculator
// ============================================================================

export interface MonteCarloInput {
  readonly portfolio_value_cents: number;
  readonly annual_spend_cents: number;
  /** Mean annual real return %, e.g. 7. */
  readonly expected_return_pct: number;
  /** Standard deviation of annual returns %. Default 15 (S&P historical). */
  readonly return_stddev_pct?: number;
  /** Years per simulation. Default 30. */
  readonly horizon_years?: number;
  /** Simulation count. Default 1000. */
  readonly simulations?: number;
  /** PRNG seed. Default 42 — keeps fixtures reproducible. */
  readonly seed?: number;
  /** How many sample paths to return for charting. Default 20. */
  readonly paths_sample_size?: number;
  /** Phase 6 Session 3 — withdrawal strategy override. Defaults to
   *  bengen_constant. */
  readonly withdrawal_strategy_config?: WithdrawalStrategyConfig;
  /** Headline target withdrawal rate %, used by Guyton-Klinger / dynamic. */
  readonly target_withdrawal_rate_pct?: number;
  /** Phase 6 Session 3 — Blanchett spending smile. */
  readonly spending_smile?: SpendingSmileConfig;
  /** Caller's age at year_offset = 0. Required for smile / bond-tent / VPW. */
  readonly age_at_retirement?: number;
  /** Phase 6 Session 3 — bond-tent glidepath. When set, per-year μ / σ
   *  are blended from equity / bond components below. */
  readonly bond_tent?: BondTentConfig;
  /** Phase 6 Session 3 — when bond-tent is on, the per-year blend uses
   *  these. Default equity_mean = expected_return_pct, bond_mean = 2,
   *  equity_stddev = return_stddev_pct, bond_stddev = 6. */
  readonly equity_mean_pct?: number;
  readonly bond_mean_pct?: number;
  readonly equity_stddev_pct?: number;
  readonly bond_stddev_pct?: number;
  /** Static allocation when bond-tent disabled (used for blended μ when
   *  the strategy or smile needs a per-year context). Default 60 / 40. */
  readonly equity_pct?: number;
  readonly bond_pct?: number;
  /** Current Shiller CAPE; used by CAPE-based strategy when no per-year
   *  CAPE is available. Defaults to a reasonable contemporary value
   *  (the strategy degrades to bengen-constant when missing). */
  readonly cape_anchor?: number;
  /** Added to each retirement year's spending, by year offset. Positive
   *  for extra costs, negative for income like Social Security. */
  readonly annual_adjustments_cents?: readonly number[];
  /** Tax paid on withdrawals, as % of the gross amount withdrawn. */
  readonly effective_tax_rate_pct?: number;
}

export interface MonteCarloPercentiles {
  /** Final balance at year=horizon for the 10th-percentile simulation. */
  readonly p10: number;
  readonly p25: number;
  readonly p50: number;
  readonly p75: number;
  readonly p90: number;
}

export interface MonteCarloPercentileBand {
  readonly year_offset: number;
  readonly p10_cents: number;
  readonly p50_cents: number;
  readonly p90_cents: number;
}

export interface MonteCarloResult {
  readonly success_pct: number;
  /** Final-balance percentiles in cents. */
  readonly final_percentiles: MonteCarloPercentiles;
  /** Per-year p10/p50/p90 bands for the confidence-band chart. */
  readonly band: readonly MonteCarloPercentileBand[];
  readonly worst_case_cents: number;
  readonly best_case_cents: number;
  /** Sample of full paths for chart overlay. */
  readonly paths_sample: readonly (readonly BalancePoint[])[];
  /** What the helper actually ran with (after defaults applied). */
  readonly resolved: {
    readonly simulations: number;
    readonly horizon_years: number;
    readonly return_stddev_pct: number;
    readonly seed: number;
  };
}

export function monteCarloSimulation(input: MonteCarloInput): MonteCarloResult {
  const {
    portfolio_value_cents,
    annual_spend_cents,
    expected_return_pct,
    return_stddev_pct = 15,
    horizon_years = 30,
    simulations = 1000,
    seed = 42,
    paths_sample_size = 20,
    withdrawal_strategy_config,
    target_withdrawal_rate_pct,
    spending_smile,
    age_at_retirement,
    bond_tent,
    equity_mean_pct,
    bond_mean_pct = 2,
    equity_stddev_pct,
    bond_stddev_pct = 6,
    equity_pct = 60,
    bond_pct = 40,
    cape_anchor,
    annual_adjustments_cents,
    effective_tax_rate_pct = 0,
  } = input;

  if (simulations < 1) {
    throw new Error(
      `monteCarloSimulation: simulations must be >= 1 (got ${simulations})`,
    );
  }
  if (horizon_years < 1) {
    throw new Error(
      `monteCarloSimulation: horizon_years must be >= 1 (got ${horizon_years})`,
    );
  }

  const rng = mulberry32(seed);
  const normal = boxMuller(rng);

  const targetWrPct =
    target_withdrawal_rate_pct ??
    (portfolio_value_cents > 0
      ? (annual_spend_cents / portfolio_value_cents) * 100
      : 4);
  // Equity component defaults to the headline expected_return_pct so 6.2
  // single-knob callers continue to behave identically.
  const eqMu = equity_mean_pct ?? expected_return_pct;
  const eqSigma = equity_stddev_pct ?? return_stddev_pct;

  // Allocate one column per simulation. Rows = year_offset 0..horizon.
  const balancesPerYear: number[][] = [];
  for (let y = 0; y <= horizon_years; y++) {
    balancesPerYear.push(new Array<number>(simulations).fill(0));
  }

  let survived = 0;
  const allFinalBalances = new Array<number>(simulations);
  const sampledPaths: BalancePoint[][] = [];
  const sampleStep = Math.max(
    1,
    Math.floor(simulations / Math.max(1, paths_sample_size)),
  );

  for (let s = 0; s < simulations; s++) {
    let balance = portfolio_value_cents;
    let prevSpend = annual_spend_cents;
    let ranOut = false;
    balancesPerYear[0]![s] = balance;
    const path: BalancePoint[] = [
      { year_offset: 0, balance_cents: balance },
    ];

    for (let y = 1; y <= horizon_years; y++) {
      // Per-year allocation: bond-tent overrides static when enabled.
      let eqW = equity_pct / 100;
      let bdW = bond_pct / 100;
      if (bond_tent && bond_tent.enabled && age_at_retirement !== undefined) {
        const alloc = bondTentAllocation({
          age: age_at_retirement + (y - 1),
          config: bond_tent,
        });
        eqW = alloc.equity_pct / 100;
        bdW = alloc.bond_pct / 100;
      }

      // Per-year stochastic return: blended μ + portfolio variance σ
      // (uncorrelated ρ=0 simplification — Phase 11 may layer
      // correlation if it's defensible).
      const muYear = eqW * eqMu + bdW * bond_mean_pct;
      const sigmaYear = Math.sqrt(
        eqW * eqW * eqSigma * eqSigma +
          bdW * bdW * bond_stddev_pct * bond_stddev_pct,
      );
      const r = muYear + sigmaYear * normal();
      balance = balance * (1 + r / 100);

      // Strategy + smile dispatch, mirrors trinityBackTest's per-year loop.
      const baseSpend = annual_spend_cents;
      const stateForStrategy = buildWithdrawalState({
        year_offset: y - 1,
        initial_spend_cents: baseSpend,
        prev_spend_cents: prevSpend,
        current_balance_cents: balance,
        target_withdrawal_rate_pct: targetWrPct,
        age_value:
          age_at_retirement === undefined
            ? undefined
            : age_at_retirement + (y - 1),
        equity_pct: eqW * 100,
        bond_pct: bdW * 100,
        cape_value: cape_anchor,
      });
      let spend = applyWithdrawalStrategy(
        stateForStrategy,
        withdrawal_strategy_config,
      );
      if (
        spending_smile &&
        spending_smile.enabled &&
        age_at_retirement !== undefined
      ) {
        const smileMultiplier =
          applySmileMultiplier(y - 1, age_at_retirement, spending_smile.curve);
        spend *= smileMultiplier;
      }

      balance -= withdrawalForYear(
        spend,
        annual_adjustments_cents?.[y - 1],
        effective_tax_rate_pct,
      );
      prevSpend = spend;
      if (balance < 0) {
        ranOut = true;
        balance = 0;
      }
      balancesPerYear[y]![s] = balance;
      path.push({ year_offset: y, balance_cents: Math.round(balance) });
    }

    if (!ranOut) survived++;
    allFinalBalances[s] = balance;

    if (s % sampleStep === 0 && sampledPaths.length < paths_sample_size) {
      sampledPaths.push(path);
    }
  }

  // Sort once for percentile lookups across all years.
  const band: MonteCarloPercentileBand[] = [];
  for (let y = 0; y <= horizon_years; y++) {
    const sorted = balancesPerYear[y]!.slice().sort((a, b) => a - b);
    band.push({
      year_offset: y,
      p10_cents: Math.round(percentile(sorted, 10)),
      p50_cents: Math.round(percentile(sorted, 50)),
      p90_cents: Math.round(percentile(sorted, 90)),
    });
  }

  const finalSorted = allFinalBalances.slice().sort((a, b) => a - b);

  return {
    success_pct: (survived / simulations) * 100,
    final_percentiles: {
      p10: Math.round(percentile(finalSorted, 10)),
      p25: Math.round(percentile(finalSorted, 25)),
      p50: Math.round(percentile(finalSorted, 50)),
      p75: Math.round(percentile(finalSorted, 75)),
      p90: Math.round(percentile(finalSorted, 90)),
    },
    band,
    worst_case_cents: Math.round(finalSorted[0] ?? 0),
    best_case_cents: Math.round(finalSorted[finalSorted.length - 1] ?? 0),
    paths_sample: sampledPaths,
    resolved: {
      simulations,
      horizon_years,
      return_stddev_pct,
      seed,
    },
  };
}

// ============================================================================
// Phase 6 Session 3 — strategy comparison helper
// ============================================================================

export interface StrategyComparisonInput {
  readonly portfolio_value_cents: number;
  readonly annual_spend_cents: number;
  readonly equity_pct: number;
  readonly bond_pct: number;
  readonly horizon_years?: number;
  readonly target_withdrawal_rate_pct?: number;
  /** Trinity input series — pass HISTORICAL_REAL_RETURNS_WITH_CAPE so the
   *  CAPE-based strategy works. Other strategies ignore the CAPE column. */
  readonly returns: readonly HistoricalRealYearWithCape[];
  /** Monte Carlo expected return / stddev (real). */
  readonly expected_return_pct: number;
  readonly return_stddev_pct?: number;
  readonly simulations?: number;
  readonly seed?: number;
  /** Strategies to compare. Defaults to all five. */
  readonly strategies?: readonly WithdrawalStrategy[];
  /** Per-strategy config knob overrides. Sparse — only the populated
   *  strategy reads its slice. */
  readonly config?: Omit<WithdrawalStrategyConfig, "strategy">;
  readonly age_at_retirement?: number;
  readonly spending_smile?: SpendingSmileConfig;
  readonly bond_tent?: BondTentConfig;
  /** Latest CAPE for Monte Carlo CAPE-based runs. */
  readonly cape_anchor?: number;
  readonly bond_mean_pct?: number;
  readonly bond_stddev_pct?: number;
  readonly annual_adjustments_cents?: readonly number[];
  readonly effective_tax_rate_pct?: number;
}

export interface StrategyComparisonRow {
  readonly strategy: WithdrawalStrategy;
  /** Trinity Study % windows surviving full horizon. */
  readonly trinity_success_pct: number;
  /** Monte Carlo % simulations surviving full horizon. */
  readonly mc_success_pct: number;
  /** Monte Carlo final-balance percentiles. */
  readonly mc_p10_end_cents: number;
  readonly mc_p50_end_cents: number;
  readonly mc_p90_end_cents: number;
}

/**
 * Run Trinity + Monte Carlo for each requested strategy and return one
 * row per strategy. Powers the StressTestPanel "Compare strategies"
 * tab. Deterministic given seed.
 */
export function compareWithdrawalStrategies(
  input: StrategyComparisonInput,
): readonly StrategyComparisonRow[] {
  const strategies: readonly WithdrawalStrategy[] = input.strategies ?? [
    "bengen_constant",
    "guyton_klinger",
    "vpw",
    "cape_based",
    "dynamic_band",
  ];
  const horizon = input.horizon_years ?? 30;
  const sims = input.simulations ?? 1000;
  const seed = input.seed ?? 42;
  const stddev = input.return_stddev_pct ?? 15;

  const rows: StrategyComparisonRow[] = [];
  for (const strategy of strategies) {
    const config: WithdrawalStrategyConfig = {
      ...(input.config ?? {}),
      strategy,
    };
    // Conditional spreads to satisfy `exactOptionalPropertyTypes`: omit
    // optional fields when undefined rather than passing `field: undefined`.
    const trinityInput: TrinityBackTestInput = {
      portfolio_value_cents: input.portfolio_value_cents,
      annual_spend_cents: input.annual_spend_cents,
      equity_pct: input.equity_pct,
      bond_pct: input.bond_pct,
      horizon_years: horizon,
      returns: input.returns,
      withdrawal_strategy_config: config,
      ...(input.target_withdrawal_rate_pct !== undefined && {
        target_withdrawal_rate_pct: input.target_withdrawal_rate_pct,
      }),
      ...(input.spending_smile && { spending_smile: input.spending_smile }),
      ...(input.age_at_retirement !== undefined && {
        age_at_retirement: input.age_at_retirement,
      }),
      ...(input.bond_tent && { bond_tent: input.bond_tent }),
      ...(input.annual_adjustments_cents && {
        annual_adjustments_cents: input.annual_adjustments_cents,
      }),
      ...(input.effective_tax_rate_pct !== undefined && {
        effective_tax_rate_pct: input.effective_tax_rate_pct,
      }),
    };
    const trinity = trinityBackTest(trinityInput);
    const mcInput: MonteCarloInput = {
      portfolio_value_cents: input.portfolio_value_cents,
      annual_spend_cents: input.annual_spend_cents,
      expected_return_pct: input.expected_return_pct,
      return_stddev_pct: stddev,
      horizon_years: horizon,
      simulations: sims,
      seed,
      withdrawal_strategy_config: config,
      equity_pct: input.equity_pct,
      bond_pct: input.bond_pct,
      ...(input.target_withdrawal_rate_pct !== undefined && {
        target_withdrawal_rate_pct: input.target_withdrawal_rate_pct,
      }),
      ...(input.spending_smile && { spending_smile: input.spending_smile }),
      ...(input.age_at_retirement !== undefined && {
        age_at_retirement: input.age_at_retirement,
      }),
      ...(input.bond_tent && { bond_tent: input.bond_tent }),
      ...(input.cape_anchor !== undefined && { cape_anchor: input.cape_anchor }),
      ...(input.bond_mean_pct !== undefined && {
        bond_mean_pct: input.bond_mean_pct,
      }),
      ...(input.bond_stddev_pct !== undefined && {
        bond_stddev_pct: input.bond_stddev_pct,
      }),
      ...(input.annual_adjustments_cents && {
        annual_adjustments_cents: input.annual_adjustments_cents,
      }),
      ...(input.effective_tax_rate_pct !== undefined && {
        effective_tax_rate_pct: input.effective_tax_rate_pct,
      }),
    };
    const mc = monteCarloSimulation(mcInput);
    rows.push({
      strategy,
      trinity_success_pct: trinity.success_pct,
      mc_success_pct: mc.success_pct,
      mc_p10_end_cents: mc.final_percentiles.p10,
      mc_p50_end_cents: mc.final_percentiles.p50,
      mc_p90_end_cents: mc.final_percentiles.p90,
    });
  }
  return rows;
}

// ============================================================================
// Internal: PRNG + percentile + Box-Muller
// ============================================================================

/**
 * Mulberry32 — small, fast, deterministic 32-bit PRNG. Good enough for
 * Monte Carlo retirement simulations (statistical properties published in
 * the original Tommy Ettinger gist). Returns floats in [0, 1).
 */
function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return function () {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/**
 * Box-Muller transform: turns two uniform(0,1) samples into one
 * normal(0,1) sample. Closed-form, fast, numerically stable for our range.
 *
 * We discard the second normal sample (Box-Muller produces two per call).
 * For 1000-sim Monte Carlo this is negligible.
 */
function boxMuller(rng: () => number): () => number {
  return function () {
    const u1 = Math.max(rng(), Number.EPSILON); // avoid log(0)
    const u2 = rng();
    return Math.sqrt(-2 * Math.log(u1)) * Math.cos(2 * Math.PI * u2);
  };
}

/**
 * Linear-interpolation percentile from a sorted array. p in [0, 100].
 * Matches numpy/Excel default ("linear") definition.
 */
function percentile(sorted: readonly number[], p: number): number {
  if (sorted.length === 0) return 0;
  if (sorted.length === 1) return sorted[0]!;
  const rank = (p / 100) * (sorted.length - 1);
  const lo = Math.floor(rank);
  const hi = Math.ceil(rank);
  if (lo === hi) return sorted[lo]!;
  const frac = rank - lo;
  return sorted[lo]! + (sorted[hi]! - sorted[lo]!) * frac;
}

/**
 * Build a `WithdrawalState` with conditional spreads — required because
 * `exactOptionalPropertyTypes: true` forbids `field: undefined` on
 * optional properties.
 */
function buildWithdrawalState(input: {
  readonly year_offset: number;
  readonly initial_spend_cents: number;
  readonly prev_spend_cents: number;
  readonly current_balance_cents: number;
  readonly target_withdrawal_rate_pct: number;
  readonly age_value: number | undefined;
  readonly equity_pct: number;
  readonly bond_pct: number;
  readonly cape_value: number | undefined;
}): WithdrawalState {
  return {
    year_offset: input.year_offset,
    initial_spend_cents: input.initial_spend_cents,
    prev_spend_cents: input.prev_spend_cents,
    current_balance_cents: input.current_balance_cents,
    target_withdrawal_rate_pct: input.target_withdrawal_rate_pct,
    equity_pct: input.equity_pct,
    bond_pct: input.bond_pct,
    ...(input.age_value !== undefined && { age: input.age_value }),
    ...(input.cape_value !== undefined && { cape_value: input.cape_value }),
  };
}

/**
 * Compute the smile multiplier for a given (year_offset, retirement_age,
 * curve) without materializing a fake initial_spend. Returns ~1.0 at
 * year 0 and dips/rises per the Blanchett curve.
 */
function applySmileMultiplier(
  year_offset: number,
  age_at_retirement: number,
  curve: SpendingSmileConfig["curve"],
): number {
  const probeBase = 100_000_000;
  const adjusted = applySpendingSmile(
    curve === undefined
      ? {
          initial_spend_cents: probeBase,
          year_offset,
          age_at_retirement,
        }
      : {
          initial_spend_cents: probeBase,
          year_offset,
          age_at_retirement,
          curve,
        },
  );
  return adjusted / probeBase;
}
